/**
 * TimePickerModal Component
 *
 * A modal-based time picker with separate hour and minute controls.
 * Provides a touch-friendly interface with +/- buttons for easy adjustment.
 *
 * Features:
 * - Large touch-friendly controls
 * - Separate hour and minute inputs
 * - +/- buttons for quick adjustments
 * - Direct number input support
 * - Validation (hours 0-23, minutes 0-59)
 * - Cancel/Done actions
 */

import { useState, useEffect } from 'react';

interface TimePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMinutes: number; // Total minutes
  onSave: (totalMinutes: number) => void;
  title?: string;
}

export default function TimePickerModal({
  isOpen,
  onClose,
  initialMinutes,
  onSave,
  title = 'Set Duration',
}: TimePickerModalProps) {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);

  // Initialize from total minutes
  useEffect(() => {
    if (isOpen) {
      const h = Math.floor(initialMinutes / 60);
      const m = initialMinutes % 60;
      setHours(h);
      setMinutes(m);
    }
  }, [isOpen, initialMinutes]);

  const handleHourChange = (delta: number) => {
    setHours(prev => {
      const newValue = prev + delta;
      if (newValue < 0) return 0;
      if (newValue > 23) return 23;
      return newValue;
    });
  };

  const handleMinuteChange = (delta: number) => {
    setMinutes(prev => {
      const newValue = prev + delta;
      if (newValue < 0) return 0;
      if (newValue > 59) return 59;
      return newValue;
    });
  };

  const handleHourInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value)) {
      setHours(0);
    } else if (value < 0) {
      setHours(0);
    } else if (value > 23) {
      setHours(23);
    } else {
      setHours(value);
    }
  };

  const handleMinuteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value)) {
      setMinutes(0);
    } else if (value < 0) {
      setMinutes(0);
    } else if (value > 59) {
      setMinutes(59);
    } else {
      setMinutes(value);
    }
  };

  const handleSave = () => {
    const totalMinutes = hours * 60 + minutes;
    onSave(totalMinutes);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6">
        {/* Header */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 text-center">
          {title}
        </h2>

        {/* Time Picker Controls */}
        <div className="flex gap-6 justify-center mb-8">
          {/* Hours */}
          <div className="flex flex-col items-center">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Hours
            </label>
            <button
              type="button"
              onClick={() => handleHourChange(1)}
              className="w-14 h-14 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-2xl font-bold mb-2 active:scale-95 transition-transform"
              aria-label="Increase hours"
            >
              +
            </button>
            <input
              type="number"
              value={hours}
              onChange={handleHourInputChange}
              className="w-20 h-16 text-center text-3xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800"
              min="0"
              max="23"
              aria-label="Hours"
            />
            <button
              type="button"
              onClick={() => handleHourChange(-1)}
              className="w-14 h-14 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-2xl font-bold mt-2 active:scale-95 transition-transform"
              aria-label="Decrease hours"
            >
              −
            </button>
          </div>

          {/* Separator */}
          <div className="flex items-center text-4xl font-bold text-gray-400 dark:text-gray-500">
            :
          </div>

          {/* Minutes */}
          <div className="flex flex-col items-center">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Minutes
            </label>
            <button
              type="button"
              onClick={() => handleMinuteChange(1)}
              className="w-14 h-14 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-2xl font-bold mb-2 active:scale-95 transition-transform"
              aria-label="Increase minutes"
            >
              +
            </button>
            <input
              type="number"
              value={minutes.toString().padStart(2, '0')}
              onChange={handleMinuteInputChange}
              className="w-20 h-16 text-center text-3xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800"
              min="0"
              max="59"
              aria-label="Minutes"
            />
            <button
              type="button"
              onClick={() => handleMinuteChange(-1)}
              className="w-14 h-14 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-2xl font-bold mt-2 active:scale-95 transition-transform"
              aria-label="Decrease minutes"
            >
              −
            </button>
          </div>
        </div>

        {/* Quick increments */}
        <div className="mb-6">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center">
            Quick add minutes:
          </p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={() => handleMinuteChange(5)}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
            >
              +5m
            </button>
            <button
              type="button"
              onClick={() => handleMinuteChange(10)}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
            >
              +10m
            </button>
            <button
              type="button"
              onClick={() => handleMinuteChange(15)}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
            >
              +15m
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 px-4 py-3 text-base font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
